import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';

type AgentSendIdentity = {
	id: string;
	named_id?: string;
};

type DeriveAgentSendRequestIdentityParams = {
	conversationAgent: AgentSendIdentity;
	defaultAgent: AgentSendIdentity;
	explicitAgentId?: string;
	inputSelectedAgentIds?: string[];
	omitConversationDefaultAgentRequestFields?: boolean;
	/**
	 * Whether the stored chat input selected-agent draft is eligible to route this send.
	 *
	 * The active selected-agent id is the last entry in `inputSelectedAgentIds`; it is an
	 * unsent chat input selected-agent draft. It only becomes the routing agent when the send
	 * path explicitly opts in (currently `messageSources.interactionSource === 'chatInput'`).
	 * Sends that originate elsewhere (e.g. a Confluence inline Rovo input handing
	 * the message to the sidebar via pubsub) must NOT inherit the sidebar's unsent
	 * draft — they fall back to the conversation agent unless they pass
	 * an explicit `agentId` (which always wins). Defaults to `false` so only chat-input-originated sends opt in.
	 */
	allowSelectedAgentOverride?: boolean;
};

export type AgentSendRequestIdentity = {
	/**
	 * The chat input selected-agent draft that actually routed this send. Undefined when the
	 * send used an explicit caller agent or fell back to the conversation agent.
	 */
	routedSelectedAgentId?: string;
	routingAgentId: string;
	agentIdForRequest?: string;
	agentNamedIdForRequest?: string;
};

const getSelectedAgentIdForSend = (
	inputSelectedAgentIds?: string[],
	allowSelectedAgentOverride = false,
): string | undefined => {
	const activeSelectedAgentDraftId = inputSelectedAgentIds?.at(-1)?.trim();

	if (
		activeSelectedAgentDraftId &&
		allowSelectedAgentOverride &&
		isExperimentEnabled('rovo_chat_mention_agents')
	) {
		return activeSelectedAgentDraftId;
	}

	return undefined;
};

export const deriveAgentSendRequestIdentity = ({
	conversationAgent,
	defaultAgent,
	explicitAgentId,
	omitConversationDefaultAgentRequestFields = false,
	inputSelectedAgentIds,
	allowSelectedAgentOverride = false,
}: DeriveAgentSendRequestIdentityParams): AgentSendRequestIdentity => {
	const mentionAgentsExperimentEnabled = isExperimentEnabled('rovo_chat_mention_agents');

	// Preserve the former all-flags-off behavior for explicit caller-provided ids,
	// including empty and whitespace-only values.
	if (!mentionAgentsExperimentEnabled && explicitAgentId !== undefined) {
		const isExplicitDefaultAgent = explicitAgentId === defaultAgent.id;
		return {
			routedSelectedAgentId: undefined,
			routingAgentId: explicitAgentId,
			agentIdForRequest: isExplicitDefaultAgent ? undefined : explicitAgentId,
			agentNamedIdForRequest: isExplicitDefaultAgent ? undefined : conversationAgent.named_id,
		};
	}

	const normalizedExplicitAgentId = explicitAgentId || undefined;
	const routedSelectedAgentId = normalizedExplicitAgentId
		? undefined
		: getSelectedAgentIdForSend(inputSelectedAgentIds, allowSelectedAgentOverride);
	const routingAgentId = normalizedExplicitAgentId ?? routedSelectedAgentId ?? conversationAgent.id;

	if (normalizedExplicitAgentId) {
		// Explicit caller-provided agent id wins so non-chat flows are not
		// regressed by stored selected-agent state. Preserve the conversation agent's
		// named id for callers that still rely on named-id semantics.
		const isExplicitDefaultAgent = normalizedExplicitAgentId === defaultAgent.id;
		return {
			routedSelectedAgentId: undefined,
			routingAgentId,
			agentIdForRequest: isExplicitDefaultAgent ? undefined : normalizedExplicitAgentId,
			agentNamedIdForRequest: isExplicitDefaultAgent ? undefined : conversationAgent.named_id,
		};
	}

	if (routedSelectedAgentId) {
		// Selected-agent override is sent for routing; if the user explicitly
		// selected default Rovo we omit request fields so the backend treats
		// it as a normal default Rovo send.
		const isSelectedDefaultAgent = routedSelectedAgentId === defaultAgent.id;
		return {
			routedSelectedAgentId,
			routingAgentId,
			agentIdForRequest: isSelectedDefaultAgent ? undefined : routedSelectedAgentId,
			agentNamedIdForRequest: isSelectedDefaultAgent ? undefined : routedSelectedAgentId,
		};
	}

	// No override preserves the caller/conversation request identity. The
	// `omitConversationDefaultAgentRequestFields` option is compatibility for the main
	// conversation-assistant wire contract and should remain when the flag is removed.
	const shouldOmitConversationDefaultAgentRequestFields =
		omitConversationDefaultAgentRequestFields && conversationAgent.id === defaultAgent.id;
	return {
		routedSelectedAgentId: undefined,
		routingAgentId,
		agentIdForRequest: shouldOmitConversationDefaultAgentRequestFields
			? undefined
			: conversationAgent.id,
		agentNamedIdForRequest: shouldOmitConversationDefaultAgentRequestFields
			? undefined
			: conversationAgent.named_id,
	};
};
